import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { ArtistModel, ItemModel, ListingModel, PaginationModel } from 'models';
import { ArtistGrid, ItemGrid, ListingGrid, Wizard } from 'components';
import { ArtistGridSelectionType } from 'components/Artist/ArtistGrid/ArtistGrid';
import { ItemGridSelectionType } from 'components/Item/ItemGrid/ItemGrid';
import { ListingGridSelectionType } from 'components/Listing/ListingGrid/ListingGrid';
import { useArtists, useArtistItems } from 'hooks/queries/useArtists';
import { useArtistPendingListings, useListingSuggestionsForItem } from 'hooks/queries/useListings';
import { AiFillCheckCircle } from 'react-icons/ai';

type GroupingWorkflow = 'item-first' | 'listing-first';

export interface GroupingCreationWizardProps {
  defaultArtistId?: string;
  onSubmit: (item: ItemModel, listings: ListingModel[]) => void;
  isLoading?: boolean;
  initialWorkflow?: GroupingWorkflow;
  defaultItem?: ItemModel | null;
  defaultListings?: ListingModel[] | null;
  preselectedListings?: ListingModel[] | null;
  itemSuggestions?: ItemModel[];
}

function uniqueListings(listings: (ListingModel | null | undefined)[]): ListingModel[] {
  const map = new Map<string, ListingModel>();
  listings.forEach((listing) => {
    if (listing) {
      map.set(listing.id, listing);
    }
  });
  return Array.from(map.values());
}

export default function GroupingCreationWizard({
  defaultArtistId,
  onSubmit,
  itemSuggestions = [],
  isLoading = false,
  initialWorkflow,
  defaultItem = null,
  defaultListings = null,
  preselectedListings = null
}: GroupingCreationWizardProps) {
  const [workflow, setWorkflow] = useState<GroupingWorkflow>(initialWorkflow ?? 'item-first');
  const [selectedArtist, setSelectedArtist] = useState<ArtistModel | null>(null);
  const defaultListingArray = useMemo<ListingModel[]>(() => uniqueListings(defaultListings ?? []), [defaultListings]);
  const preselectedListingArray = useMemo<ListingModel[]>(() => uniqueListings(preselectedListings ?? []), [preselectedListings]);
  const [selectedItem, setSelectedItem] = useState<ItemModel | null>(defaultItem);
  const [selectedListings, setSelectedListings] = useState<ListingModel[]>(preselectedListingArray);
  const [listingsPage, setListingsPage] = useState(1);
  const [itemSuggestionListings, setItemSuggestionListings] = useState<ListingModel[]>(defaultListingArray);
  const [pendingListingResults, setPendingListingResults] = useState<ListingModel[]>(defaultListingArray);
  const [shouldApplyInitialStep, setShouldApplyInitialStep] = useState(true);

  const { data: artists = [], isLoading: artistsLoading } = useArtists();

  const activeArtistId = useMemo(
    () => selectedArtist?.id || defaultArtistId || '',
    [selectedArtist, defaultArtistId]
  );

  const {
    data: artistItems = [],
    isLoading: artistItemsLoading,
    isFetching: artistItemsFetching
  } = useArtistItems(activeArtistId, {
    enabled: !!activeArtistId && itemSuggestions.length === 0
  });

  console.log('artistItems', artistItems);

  useEffect(() => {
    if (initialWorkflow && initialWorkflow !== workflow) {
      setWorkflow(initialWorkflow);
    }
  }, [initialWorkflow, workflow]);

  useEffect(() => {
    if (defaultArtistId && artists.length > 0) {
      const artist = artists.find((artistItem) => artistItem.id === defaultArtistId);
      if (artist) {
        setSelectedArtist(artist);
      }
    }
  }, [defaultArtistId, artists]);

  useEffect(() => {
    if (!selectedArtist && defaultItem?.artist instanceof ArtistModel) {
      setSelectedArtist(defaultItem.artist);
    }
  }, [selectedArtist, defaultItem]);

  const availableDefaultsForArtist = useMemo(() => {
    if (!activeArtistId) {
      return defaultListingArray;
    }
    return defaultListingArray.filter((listing) => listing.artistId === activeArtistId);
  }, [defaultListingArray, activeArtistId]);

  const availableDefaultsForItem = useMemo(() => {
    if (!selectedItem) {
      return availableDefaultsForArtist;
    }

    const associatedDefaults = defaultListingArray.filter((listing) => {
      try {
        return listing.getItemIds().includes(selectedItem.id);
      } catch {
        return false;
      }
    });

    return associatedDefaults.length ? associatedDefaults : availableDefaultsForArtist;
  }, [defaultListingArray, availableDefaultsForArtist, selectedItem]);

  useEffect(() => {
    setSelectedItem((previous) => {
      if (!defaultItem) {
        if (previous && activeArtistId && previous.artistId !== activeArtistId) {
          return null;
        }
        return previous;
      }

      const matchesArtist = !activeArtistId || defaultItem.artistId === activeArtistId;
      if (matchesArtist) {
        return defaultItem;
      }

      if (previous && activeArtistId && previous.artistId === activeArtistId) {
        return previous;
      }

      return null;
    });
  }, [defaultItem, activeArtistId]);

  useEffect(() => {
    if (workflow !== 'item-first') {
      return;
    }
    setListingsPage(1);
    setItemSuggestionListings(availableDefaultsForItem);
    setSelectedListings((previous) => {
      if (!selectedItem) {
        return uniqueListings(preselectedListingArray);
      }

      const matchingSelections = previous.filter((listing) => {
        try {
          return listing.getItemIds().includes(selectedItem.id);
        } catch {
          return false;
        }
      });

      const preselectedMatches = preselectedListingArray.filter((listing) => {
        try {
          return listing.getItemIds().includes(selectedItem.id);
        } catch {
          return false;
        }
      });

      return uniqueListings([...preselectedMatches, ...matchingSelections]);
    });
  }, [workflow, availableDefaultsForItem, selectedItem, preselectedListingArray]);

  useEffect(() => {
    if (workflow !== 'listing-first') {
      return;
    }
    setListingsPage(1);
    setPendingListingResults(availableDefaultsForArtist);
    if (activeArtistId) {
      setSelectedListings((previous) => {
        const matchingSelections = previous.filter((listing) => listing.artistId === activeArtistId);
        const preselectedMatches = preselectedListingArray.filter((listing) => listing.artistId === activeArtistId);
        return uniqueListings([...preselectedMatches, ...matchingSelections]);
      });
    }
  }, [workflow, availableDefaultsForArtist, activeArtistId, preselectedListingArray]);

  useEffect(() => {
    if (workflow === 'item-first' && selectedItem) {
      setListingsPage(1);
    }
  }, [workflow, selectedItem]);

  useEffect(() => {
    if (workflow === 'listing-first' && activeArtistId) {
      setListingsPage(1);
    }
  }, [workflow, activeArtistId]);

  const mergeListings = useCallback((existing: ListingModel[], incoming: ListingModel[]) => {
    if (!incoming.length) {
      return existing;
    }

    const map = new Map(existing.map((listing) => [listing.id, listing]));
    incoming.forEach((listing) => {
      if (!map.has(listing.id)) {
        map.set(listing.id, listing);
      }
    });

    return Array.from(map.values());
  }, []);

  const listingSuggestionQuery = selectedItem?.getDisplayName() ?? '';
  const listingSuggestionArtist = selectedItem?.getArtistName() ?? '';
  const enableListingSuggestions = workflow === 'item-first' && !!selectedItem;

  const {
    data: listingSuggestionsPageData,
    isLoading: listingSuggestionsLoading,
    isFetching: listingSuggestionsFetching
  } = useListingSuggestionsForItem(
    listingSuggestionQuery,
    listingSuggestionArtist,
    listingsPage,
    enableListingSuggestions
  );

  const enablePendingListings = workflow === 'listing-first' && !!activeArtistId;
  const {
    data: pendingListingsPageData,
    isLoading: pendingListingsLoading,
    isFetching: pendingListingsFetching
  } = useArtistPendingListings(activeArtistId, listingsPage, {
    enabled: enablePendingListings,
    placeholderData: (previousData) => previousData
  });

  useEffect(() => {
    if (workflow !== 'item-first') {
      return;
    }
    if (!(listingSuggestionsPageData instanceof PaginationModel)) {
      return;
    }
    const pageListings = listingSuggestionsPageData.data || [];
    setItemSuggestionListings((previous) => {
      const base = listingsPage === 1 ? availableDefaultsForItem : previous;
      return mergeListings(base, pageListings);
    });
  }, [workflow, listingSuggestionsPageData, listingsPage, mergeListings, availableDefaultsForItem]);

  useEffect(() => {
    if (workflow !== 'listing-first') {
      return;
    }
    if (!(pendingListingsPageData instanceof PaginationModel)) {
      return;
    }
    const pageListings = pendingListingsPageData.data || [];
    setPendingListingResults((previous) => {
      const base = listingsPage === 1 ? availableDefaultsForArtist : previous;
      return mergeListings(base, pageListings);
    });
  }, [workflow, pendingListingsPageData, listingsPage, mergeListings, availableDefaultsForArtist]);

  const listingsForSelection = workflow === 'item-first' ? itemSuggestionListings : pendingListingResults;

  const listingsHasMore = useMemo(() => {
    if (workflow === 'item-first') {
      if (!enableListingSuggestions) {
        return false;
      }
      return listingSuggestionsPageData instanceof PaginationModel && listingSuggestionsPageData.hasMorePages();
    }

    if (workflow === 'listing-first') {
      if (!enablePendingListings) {
        return false;
      }
      return pendingListingsPageData instanceof PaginationModel && pendingListingsPageData.hasMorePages();
    }

    return false;
  }, [workflow, enableListingSuggestions, enablePendingListings, listingSuggestionsPageData, pendingListingsPageData]);

  const isListingFetchInFlight = workflow === 'item-first' ? listingSuggestionsFetching : pendingListingsFetching;

  const handleLoadMoreListings = useCallback(() => {
    if (!listingsHasMore || isListingFetchInFlight) {
      return;
    }
    setListingsPage((page) => page + 1);
  }, [listingsHasMore, isListingFetchInFlight]);

  const handleWizardSubmit = () => {
    if (selectedItem && selectedListings.length > 0) {
      onSubmit(selectedItem, selectedListings);
    }
  };

  const handleListingSelection = useCallback((listing: ListingModel) => {
    setSelectedListings((previous) => {
      const exists = previous.some((selected) => selected.id === listing.id);
      if (exists) {
        return previous.filter((selected) => selected.id !== listing.id);
      }
      return [...previous, listing];
    });
  }, []);

  const isItemsStepLoading = artistItemsLoading || artistItemsFetching || isLoading;
  const listingStepLoading = workflow === 'item-first'
    ? (listingSuggestionsLoading && listingsPage === 1 && !itemSuggestionListings.length)
    : (pendingListingsLoading && listingsPage === 1 && !pendingListingResults.length);
  const isItemStepComplete = !!selectedItem;
  const isListingStepComplete = selectedListings.length > 0;

  const workflowToggleBg = useColorModeValue('gray.100', 'gray.700');

  const renderWorkflowToggle = () => (
    <Flex
      align="center"
      justify="center"
      gap={2}
      borderRadius="lg"
      bg={workflowToggleBg}
      p={2}
      mb={4}
    >
      <Button
        variant={workflow === 'item-first' ? 'solid' : 'ghost'}
        size="sm"
        onClick={() => setWorkflow('item-first')}
      >
        Item First
      </Button>
      <Button
        variant={workflow === 'listing-first' ? 'solid' : 'ghost'}
        size="sm"
        onClick={() => setWorkflow('listing-first')}
      >
        Listing First
      </Button>
    </Flex>
  );

  const renderArtistSelection = useCallback(() => (
    <Stack spacing={4}>
      <ArtistGrid
        artists={artists}
        isLoading={artistsLoading}
        showFilters={true}
        showSort={false}
        gridDisplay={true}
        gridSelection={ArtistGridSelectionType.SINGLE}
        defaultSelectedArtists={selectedArtist ? [selectedArtist] : []}
        onArtistCardClick={setSelectedArtist}
      />
    </Stack>
  ), [artists, artistsLoading, selectedArtist, setSelectedArtist]);

  const renderItemSelection = useCallback(() => (
    <Stack spacing={4}>
      {workflow === 'listing-first' && selectedListings.length > 0 && (
        <Accordion allowToggle defaultIndex={[0]}>
          <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Heading size="sm">Selected Listings ({selectedListings.length})</Heading>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Box maxHeight="200px" overflow="auto">
                <ListingGrid
                  gridListings={selectedListings}
                  isLoading={false}
                  showFilters={false}
                  gridSelection={ListingGridSelectionType.NONE}
                  showAssociatedItems={false}
                  showAssociationAction={false}
                />
              </Box>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
      <ItemGrid
        items={itemSuggestions.length > 0 ? itemSuggestions : artistItems}
        isLoading={isItemsStepLoading}
        showFilters={true}
        gridDisplay={true}
        gridMaxHeight={400}
        gridSelection={ItemGridSelectionType.SINGLE}
        defaultSelectedItems={selectedItem ? [selectedItem] : []}
        onItemCardClick={(item) => {
          setSelectedItem(item);
        }}
        paginate={false}
        showMetadata={true}
        defaultMetadataExpanded={true}
      />
    </Stack>
  ), [workflow, selectedListings, artistItems, itemSuggestions, isItemsStepLoading, selectedArtist, selectedItem, renderWorkflowToggle, setSelectedItem]);

  const renderListingSelection = useCallback(() => (
    <Stack spacing={4}>
      {workflow === 'item-first' && selectedItem && (
        <Accordion allowToggle defaultIndex={[0]}>
          <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Heading size="sm">Selected Item</Heading>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <ItemGrid
                items={[selectedItem]}
                isLoading={false}
                showFilters={false}
                gridDisplay={true}
                paginate={false}
                showMetadata={true}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
      <ListingGrid
        gridListings={listingsForSelection}
        isLoading={listingStepLoading || (isListingFetchInFlight && !listingsForSelection.length)}
        showFilters={true}
        gridSelection={ListingGridSelectionType.MULTI}
        defaultSelectedListings={selectedListings}
        onListingCardClick={handleListingSelection}
        showAssociatedItems={false}
        showAssociationAction={false}
      />
      {listingsHasMore && (
        <Flex justify="center">
          <Button
            onClick={handleLoadMoreListings}
            isLoading={isListingFetchInFlight}
            size="sm"
            variant="outline"
          >
            Load More Listings
          </Button>
        </Flex>
      )}
    </Stack>
  ), [
    workflow,
    selectedItem,
    renderWorkflowToggle,
    selectedArtist,
    listingsForSelection,
    listingStepLoading,
    isListingFetchInFlight,
    selectedListings,
    handleListingSelection,
    listingsHasMore,
    handleLoadMoreListings
  ]);

  const renderConfirmation = useCallback(() => (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Heading size="sm">Item</Heading>
        {selectedItem ? (
          <ItemGrid
            items={[selectedItem]}
            isLoading={isLoading}
            showFilters={false}
            gridDisplay={true}
            paginate={false}
            showMetadata={true}
            defaultMetadataExpanded={true}
          />
        ) : (
          <Text color="gray.500">No item selected.</Text>
        )}
      </Stack>
      <Stack spacing={2}>
        <Heading size="sm">Listings</Heading>
        <Box maxHeight="350px" overflow="auto">
          <ListingGrid
            gridListings={selectedListings}
            isLoading={isLoading}
            showFilters={false}
            showFiltersLabel={false}
            gridSelection={ListingGridSelectionType.NONE}
            showAssociatedItems={false}
            showAssociationAction={false}
          />
        </Box>
      </Stack>
    </Stack>
  ), [selectedItem, selectedListings, isLoading]);

  const renderFinishStep = useCallback(() => (
    <Stack gap={4} alignItems="center">
      <Icon as={AiFillCheckCircle} color="green.500" w={10} h={10} />
      <Heading size="sm">Grouping created successfully</Heading>
      <Text fontSize="sm" color="gray.600" textAlign="center">
        Your grouping request has been submitted and will open in a new view.
      </Text>
    </Stack>
  ), []);

  const wizardSteps = useMemo(() => {
    if (workflow === 'item-first') {
      return [
        {
          label: 'Select Artist',
          description: 'Choose an artist',
          content: renderArtistSelection(),
          requiresValidation: true,
          isValidated: !!selectedArtist || !!defaultArtistId
        },
        {
          label: 'Select Item',
          description: 'Pick the primary item',
          content: renderItemSelection(),
          requiresValidation: true,
          isValidated: isItemStepComplete
        },
        {
          label: 'Select Listings',
          description: 'Choose listings to group',
          content: renderListingSelection(),
          requiresValidation: true,
          isValidated: isListingStepComplete
        },
        {
          label: 'Confirm',
          description: 'Review and submit',
          content: renderConfirmation(),
          requiresValidation: false,
          isValidated: true
        }
      ];
    }

    return [
      {
        label: 'Select Artist',
        description: 'Choose an artist',
        content: renderArtistSelection(),
        requiresValidation: true,
        isValidated: !!selectedArtist || !!defaultArtistId
      },
      {
        label: 'Select Listings',
        description: 'Choose listings to group',
        content: renderListingSelection(),
        requiresValidation: true,
        isValidated: isListingStepComplete
      },
      {
        label: 'Select Item',
        description: 'Pick the primary item',
        content: renderItemSelection(),
        requiresValidation: true,
        isValidated: isItemStepComplete
      },
      {
        label: 'Confirm',
        description: 'Review and submit',
        content: renderConfirmation(),
        requiresValidation: false,
        isValidated: true
      }
    ];
  }, [
    workflow,
    renderArtistSelection,
    selectedArtist,
    defaultArtistId,
    renderItemSelection,
    isItemStepComplete,
    renderListingSelection,
    isListingStepComplete,
    renderConfirmation
  ]);

  const firstIncompleteStepIndex = useMemo(() => {
    return wizardSteps.findIndex((step) => step.requiresValidation && !step.isValidated);
  }, [wizardSteps]);

  const fallbackInitialStep = useMemo(() => {
    const preConfirmIndex = Math.max(0, wizardSteps.length - 2);
    return Math.min(preConfirmIndex, Math.max(0, wizardSteps.length - 1));
  }, [wizardSteps.length]);

  const initialWizardStep = useMemo(() => {
    if (firstIncompleteStepIndex !== -1) {
      return firstIncompleteStepIndex;
    }
    return fallbackInitialStep;
  }, [firstIncompleteStepIndex, fallbackInitialStep]);

  const shouldLockPreviousSteps = useMemo(() => {
    if (initialWizardStep <= 0) {
      return false;
    }

    if (workflow === 'item-first') {
      return !!defaultItem;
    }

    return !!(defaultListings && defaultListings.length);
  }, [initialWizardStep, workflow, defaultItem, defaultListings]);

  const minStep = shouldLockPreviousSteps ? initialWizardStep : 0;

  useEffect(() => {
    setShouldApplyInitialStep(true);
  }, [initialWorkflow, defaultItem, defaultListings, workflow, initialWizardStep]);

  const handleStepChange = useCallback(() => {
    if (shouldApplyInitialStep) {
      setShouldApplyInitialStep(false);
    }
  }, [shouldApplyInitialStep]);

  return (
    <Wizard
      steps={wizardSteps}
      finishStep={renderFinishStep()}
      onSubmit={handleWizardSubmit}
      showStepper
      initialStep={shouldApplyInitialStep ? initialWizardStep : undefined}
      minStep={minStep}
      onStepChange={handleStepChange}
    />
  );
}
